import React, { FormEvent, useEffect, useState, memo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { useLocation, useNavigate } from 'react-router-dom'

import { theme } from '../../../lib/theme'
import currentUser from '../../../lib/currentUser'
import { LandingPageLayout } from '../../../lib/landing-page-layout'
import { signOut } from '../../../lib/sign-out'
import { CompanyPreview, useCompanyGetAdministratedCompaniesQuery } from '../../../common/redux/api/web-api'
import { triggerAuthChange } from '../../../common/utils/broadcastChannel'
import { RootState } from '../../redux/store'
import { setUser } from '../../../common/redux/features/user/userSlice'
import useText from '../../../common/hooks/useText'
import { FormHeader } from '../../../lib/form-header'

export type SelectOrganisationPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const SelectOrganisationPage = memo(function SelectOrganisationPage({
    className,
    style,
}: SelectOrganisationPageProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const location = useLocation()
    const from = location?.state?.from || '/'

    const [selectedOrganisation, setSelectedOrganisation] = useState<CompanyPreview | null>(null)
    const { data: organisations, isLoading } = useCompanyGetAdministratedCompaniesQuery()

    const dispatch = useDispatch()

    useEffect(() => {
        // handle logged in scenario
        const user = currentUser()
        if (user?.selectedCompanyId) {
            navigate(from || '/dashboard', { replace: true })
        }
    }, [from, navigate])

    const signedInUser = useSelector((state: RootState) => state.user)
    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        if (!signedInUser || !selectedOrganisation) {
            navigate('/login', { replace: true })
            return
        }

        const userAfterSelectCompany = { ...signedInUser, selectedCompanyId: selectedOrganisation?.id ?? '' }
        currentUser(userAfterSelectCompany)
        dispatch(setUser(userAfterSelectCompany))
        navigate(from || '/dashboard', { replace: true })
        triggerAuthChange()
    }

    const organisationOptions = organisations?.map((organisation) => ({
        label: organisation?.name,
        value: organisation,
    }))

    return (
        <LandingPageLayout
            className={cn(className)}
            style={style}
            background={theme.whiteBackground}
            hideLanguageSelect={true}
        >
            <div className="sm:w-8 md:w-6 w-11">
                <FormHeader className="mb-8" />
                <form onSubmit={handleSubmit} noValidate>
                    <Dropdown
                        className="w-full"
                        options={organisationOptions}
                        value={selectedOrganisation}
                        onChange={(option) => setSelectedOrganisation(option.value)}
                        loading={isLoading}
                        filter
                        virtualScrollerOptions={{ itemSize: 38 }}
                        placeholder={text('lblSelectOrganisation')}
                    ></Dropdown>
                    <div className="flex flex-column gap-4 justify-content-center align-items-center">
                        <Button
                            disabled={!selectedOrganisation || isLoading}
                            type="submit"
                            className="mt-5"
                            label={text('btnNext')}
                            iconPos="right"
                            icon="pi pi-arrow-right"
                        ></Button>
                        <Button
                            link
                            label={text('btnSignOut')}
                            data-testid="btn-logout"
                            onClick={() => {
                                signOut()
                            }}
                        ></Button>
                    </div>
                </form>
            </div>
        </LandingPageLayout>
    )
})
